import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { getOrderSummaryMarkdown } from "../../../api/order";
import { validateArrayIncludes } from "../../../common/validators";
import {
  ORDER_STATUS_DISPATCHABLE,
  ORDER_STATUS_FINISHABLE,
} from "../../../types/order";
import {
  acceptOrderThunk,
  finishOrderThunk,
  readyToPickupOrderThunk,
} from "../../../store/actions/ordersSlice";

// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, keyframes, styled, useTheme } from "@mui/material/styles";

// Icons
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentsIcon from "@mui/icons-material/Payments";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import SendIcon from "@mui/icons-material/Send";

// ─── Inline markdown (bold, italic, code) ─────────────────────────────────

function renderInlineMarkdown(text) {
  if (!text) return text;
  const parts = [];
  const regex = /(\*\*(.+?)\*\*)|(_(.+?)_)|(`([^`]+)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[4]}</em>);
    } else if (match[5]) {
      parts.push(
        <code key={key++} className="msg-code">
          {match[6]}
        </code>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function MessageContent({ text }) {
  const lines = text.split("\n");

  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (trimmed === "") return <Box key={i} sx={{ height: 6 }} />;

    if (trimmed.startsWith("• ")) {
      return (
        <Box key={i} sx={{ display: "flex", gap: 0.75, pl: 0.5, py: 0.15 }}>
          <span style={{ opacity: 0.4, flexShrink: 0 }}>•</span>
          <span>{renderInlineMarkdown(trimmed.slice(2))}</span>
        </Box>
      );
    }
    if (trimmed.startsWith("↳ ")) {
      return (
        <Box
          key={i}
          sx={{ pl: 2.5, py: 0.1, fontSize: "0.78rem", opacity: 0.75 }}
        >
          ↳ {renderInlineMarkdown(trimmed.slice(2))}
        </Box>
      );
    }
    if (trimmed.startsWith("📝")) {
      return (
        <Box key={i} sx={{ pl: 2.5, py: 0.1, fontSize: "0.78rem" }}>
          {renderInlineMarkdown(trimmed)}
        </Box>
      );
    }
    return (
      <Box key={i} component="span" sx={{ display: "block", py: 0.05 }}>
        {renderInlineMarkdown(trimmed)}
      </Box>
    );
  });
}

// ─── Animations ───────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeSlideInRight = keyframes`
  from { opacity: 0; transform: translateY(8px) translateX(12px); }
  to { opacity: 1; transform: translateY(0) translateX(0); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

// ─── Styled ───────────────────────────────────────────────────────────────

const SummaryRoot = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
});

const MessagesScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  minHeight: 0,
  padding: theme.spacing(2.5, 2.5, 1),
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.grey[400], 0.35),
    borderRadius: 3,
  },
}));

const ActionsFooter = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1.25, 2.5, 1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
}));

const MessagesArea = styled(Box)({
  flex: 1,
  position: "relative",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const bounceDown = keyframes`
  0%, 100% { transform: translateY(0) translateX(-50%); }
  50%       { transform: translateY(3px) translateX(-50%); }
`;

const ScrollHintBtn = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 12,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: `${theme.spacing(0.6)} ${theme.spacing(1.75)}`,
  borderRadius: 20,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  fontSize: "0.75rem",
  fontWeight: 600,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  boxShadow: `0 4px 12px ${alpha(
    theme.palette.common.black,
    0.12
  )}, 0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
  animation: `${bounceDown} 2s ease-in-out infinite`,
  transition: "box-shadow 0.15s, background-color 0.15s",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    boxShadow: `0 6px 16px ${alpha(
      theme.palette.common.black,
      0.16
    )}, 0 2px 6px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& .MuiSvgIcon-root": { fontSize: "1rem" },
}));

const ChatContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  paddingBottom: 8,
});

const ALERT_STYLES = {
  danger: (theme) => ({
    backgroundColor: alpha(theme.palette.error.main, 0.08),
    borderColor: alpha(theme.palette.error.main, 0.35),
    borderLeftWidth: 3,
    borderLeftColor: theme.palette.error.main,
    color: theme.palette.error.dark,
    "& strong": { color: theme.palette.error.dark },
  }),
  warning: (theme) => ({
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
    borderColor: alpha(theme.palette.warning.main, 0.35),
    borderLeftWidth: 3,
    borderLeftColor: theme.palette.warning.main,
    color: theme.palette.warning.dark,
    "& strong": { color: theme.palette.warning.dark },
  }),
  success: (theme) => ({
    backgroundColor: alpha(theme.palette.success.main, 0.08),
    borderColor: alpha(theme.palette.success.main, 0.35),
    borderLeftWidth: 3,
    borderLeftColor: theme.palette.success.main,
    color: theme.palette.success.dark,
    "& strong": { color: theme.palette.success.dark },
  }),
  info: (theme) => ({
    backgroundColor: alpha(theme.palette.info.main, 0.08),
    borderColor: alpha(theme.palette.info.main, 0.35),
    borderLeftWidth: 3,
    borderLeftColor: theme.palette.info.main,
    color: theme.palette.info.dark,
    "& strong": { color: theme.palette.info.dark },
  }),
};

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "alert",
})(({ theme, alert }) => ({
  maxWidth: "92%",
  padding: `${theme.spacing(1.25)} ${theme.spacing(1.75)}`,
  borderRadius: "4px 12px 12px 12px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: "0.83rem",
  lineHeight: 1.6,
  color: theme.palette.text.primary,
  animation: `${fadeSlideIn} 0.3s ease-out both`,
  "& strong": { fontWeight: 600 },
  "& em": { fontStyle: "italic", color: theme.palette.text.secondary },
  "& .msg-code": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.dark,
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: "0.8rem",
    fontFamily: "monospace",
    fontWeight: 600,
  },
  ...(alert && ALERT_STYLES[alert] ? ALERT_STYLES[alert](theme) : {}),
}));

const UserBubble = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.75)}`,
  borderRadius: "12px 12px 4px 12px",
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.dark,
  fontSize: "0.83rem",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: 6,
  animation: `${fadeSlideInRight} 0.3s ease-out both`,
}));

const AvatarDot = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { fontSize: 14 },
}));

const MessageRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
});

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  borderRadius: "4px 12px 12px 12px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 72,
  "& span": {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.text.disabled,
    display: "block",
  },
  "& span:nth-of-type(1)": {
    animation: `${dotPulse} 1.2s infinite ease-in-out`,
    animationDelay: "0s",
  },
  "& span:nth-of-type(2)": {
    animation: `${dotPulse} 1.2s infinite ease-in-out`,
    animationDelay: "0.2s",
  },
  "& span:nth-of-type(3)": {
    animation: `${dotPulse} 1.2s infinite ease-in-out`,
    animationDelay: "0.4s",
  },
}));

const ActionsRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  gap: 6,
});

const CHIP_COLOR_MAP = (theme) => ({
  primary: {
    main: theme.palette.primary.main,
    dark: theme.palette.primary.dark,
  },
  success: {
    main: theme.palette.success.main,
    dark: theme.palette.success.dark,
  },
  warning: {
    main: theme.palette.warning.main,
    dark: theme.palette.warning.dark,
  },
  danger: { main: theme.palette.error.main, dark: theme.palette.error.dark },
});

const ActionChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== "chipcolor",
})(({ theme, chipcolor }) => {
  const c = CHIP_COLOR_MAP(theme)[chipcolor] || CHIP_COLOR_MAP(theme).primary;
  return {
    borderRadius: 20,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
    backgroundColor: alpha(c.main, 0.08),
    color: c.dark,
    border: `1px solid ${alpha(c.main, 0.2)}`,
    animation: `${fadeSlideIn} 0.3s ease-out both`,
    "&:hover": { backgroundColor: alpha(c.main, 0.16) },
    "& .MuiSvgIcon-root": { fontSize: 16 },
  };
});

const LoadingState = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  gap: theme.spacing(1.5),
}));

const ErrorState = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  border: `1px dashed ${theme.palette.grey[400]}`,
}));

// ─── Hidden message helpers ───────────────────────────────────────────────

const HIDDEN_RE = /^__HIDDEN:([^:]+):(.+?)__/;
const ALERT_RE = /^__ALERT:(danger|warning|success|info)__/;

function parseMessages(rawMessages) {
  const visible = [];
  const hidden = {}; // key → { label, messages: string[] }

  for (const msg of rawMessages) {
    const hiddenMatch = msg.match(HIDDEN_RE);
    if (hiddenMatch) {
      const key = hiddenMatch[1];
      const label = hiddenMatch[2];
      const content = msg.replace(HIDDEN_RE, "");
      if (!hidden[key]) hidden[key] = { label, messages: [] };
      hidden[key].messages.push(content);
    } else {
      const alertMatch = msg.match(ALERT_RE);
      if (alertMatch) {
        visible.push({ text: msg.replace(ALERT_RE, ""), alert: alertMatch[1] });
      } else {
        visible.push({ text: msg, alert: null });
      }
    }
  }
  return { visible, hidden };
}

// ─── Component ────────────────────────────────────────────────────────────

const MESSAGE_DELAY_MS = 120;
const MAX_TOTAL_ANIMATION_MS = 600;

export default function SummaryInfos({
  orderId,
  order,
  loadOrder,
  onPrint,
  onCancel,
  onFiscal,
  onExtraDelivery,
  onToggleBlacklist,
  isBlacklisted,
  onPayment,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [allMessages, setAllMessages] = useState([]);
  const [hiddenMessages, setHiddenMessages] = useState({});
  const [visibleCount, setVisibleCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTyping, setShowTyping] = useState(false);
  const [userMessages, setUserMessages] = useState([]);
  const [revealedMessages, setRevealedMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      setError(null);
      setVisibleCount(0);
      setAllMessages([]);
      setHiddenMessages({});
      setShowTyping(false);
      setUserMessages([]);
      setRevealedMessages([]);
      const { data } = await getOrderSummaryMarkdown({ oid: orderId });
      const { visible, hidden } = parseMessages(data.messages || []);
      setAllMessages(visible);
      setHiddenMessages(hidden);
    } catch (err) {
      console.error("Error fetching order summary:", err);
      setError("Não foi possível carregar o resumo do pedido.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Reveal messages one by one
  useEffect(() => {
    if (loading || allMessages.length === 0) return;

    // Cap per-message delay so total animation never exceeds MAX_TOTAL_ANIMATION_MS
    const perDelay = Math.min(
      MESSAGE_DELAY_MS,
      Math.floor(MAX_TOTAL_ANIMATION_MS / (allMessages.length + 1))
    );

    const revealNext = () => {
      setVisibleCount((prev) => {
        const next = prev + 1;
        if (next < allMessages.length) {
          setShowTyping(true);
          timerRef.current = setTimeout(() => {
            setShowTyping(false);
            revealNext();
          }, perDelay);
        } else {
          setShowTyping(false);
        }
        return next;
      });
    };

    setShowTyping(true);
    timerRef.current = setTimeout(() => {
      setShowTyping(false);
      revealNext();
    }, perDelay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loading, allMessages]);

  // Check overflow after every render (covers content changes + resizes)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  });

  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    setHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  const handleAction = useCallback((label, icon, callback) => {
    setUserMessages((prev) => [...prev, { label, icon }]);
    if (callback) callback();
  }, []);

  const handleReveal = useCallback(
    (key, label) => {
      const entry = hiddenMessages[key];
      if (!entry) return;
      setUserMessages((prev) => [...prev, { label }]);
      setRevealedMessages((prev) => [...prev, ...entry.messages]);
    },
    [hiddenMessages]
  );

  useEffect(() => {
    if (!onPrint) return;
    const handleKeyDown = (e) => {
      if (e.key !== "p" && e.key !== "P") return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        document.activeElement?.isContentEditable
      )
        return;
      e.preventDefault();
      setUserMessages((prev) => [...prev, { label: "Imprimir Ticket" }]);
      onPrint();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onPrint]);

  const handleAcceptOrder = useCallback(() => {
    dispatch(acceptOrderThunk({ order }));
    if (loadOrder) loadOrder();
  }, [dispatch, order, loadOrder]);

  const handleReadyOrder = useCallback(() => {
    dispatch(readyToPickupOrderThunk({ order }));
    if (loadOrder) loadOrder();
  }, [dispatch, order, loadOrder]);

  const handleFinishOrder = useCallback(() => {
    dispatch(finishOrderThunk({ order }));
    if (loadOrder) loadOrder();
  }, [dispatch, order, loadOrder]);

  if (loading) {
    return (
      <LoadingState>
        <CircularProgress size={28} thickness={4} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.82rem" }}
        >
          Carregando resumo...
        </Typography>
      </LoadingState>
    );
  }

  if (error) {
    return (
      <ErrorState>
        <ErrorOutlineIcon
          sx={{ fontSize: 40, color: theme.palette.grey[400], mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" align="center">
          {error}
        </Typography>
      </ErrorState>
    );
  }

  const displayed = allMessages.slice(0, visibleCount);
  const animationDone =
    !showTyping && visibleCount >= allMessages.length && allMessages.length > 0;

  // Build available actions
  const actions = [];

  if (order?.status_number === 0) {
    actions.push({
      key: "accept",
      label: "Aceitar Pedido",
      icon: <ThumbUpIcon />,
      color: "success",
      handler: handleAcceptOrder,
    });
  }

  if (
    order?.order_type === "TAKEOUT" &&
    validateArrayIncludes(ORDER_STATUS_DISPATCHABLE, order?.status_number)
  ) {
    actions.push({
      key: "ready",
      label: "Pronto Para Retirar",
      icon: <SendIcon />,
      color: "primary",
      handler: handleReadyOrder,
    });
  }

  if (
    order?.channel === "SLAPIE_POS" &&
    validateArrayIncludes(ORDER_STATUS_FINISHABLE, order?.status_number)
  ) {
    actions.push({
      key: "finish",
      label: "Fechar Pedido",
      icon: <SendIcon />,
      color: "success",
      handler: handleFinishOrder,
    });
  }

  if (onPayment && (!order?.payment || order.payment.length === 0)) {
    actions.push({
      key: "payment",
      label: "Adicionar Pagamento",
      icon: <PaymentsIcon />,
      color: "warning",
      handler: onPayment,
    });
  }

  if (onFiscal) {
    actions.push({
      key: "fiscal",
      label: "Nota Fiscal",
      icon: <ReceiptIcon />,
      color: "primary",
      handler: onFiscal,
    });
  }
  if (onCancel) {
    actions.push({
      key: "cancel",
      label: "Cancelar Pedido",
      icon: <DeleteIcon />,
      color: "danger",
      handler: onCancel,
    });
  }
  if (onExtraDelivery) {
    actions.push({
      key: "extra",
      label: "Fazer uma Corrida Extra",
      icon: <DriveEtaIcon />,
      color: "primary",
      handler: onExtraDelivery,
    });
  }
  if (onToggleBlacklist) {
    actions.push({
      key: "blacklist",
      label: isBlacklisted
        ? "Remover da Lista de Não Confiáveis"
        : "Marcar Cliente como Não Confiável",
      icon: isBlacklisted ? <CheckCircleIcon /> : <BlockIcon />,
      color: isBlacklisted ? "success" : "danger",
      handler: onToggleBlacklist,
    });
  }
  if (onPrint) {
    actions.push({
      key: "print",
      label: "Imprimir Ticket",
      shortcut: "P",
      icon: <PrintIcon />,
      color: "primary",
      handler: onPrint,
    });
  }

  // All actions stay available — same action can be triggered multiple times
  const availableActions = actions;

  const hasFooterActions = animationDone && availableActions.length > 0;

  return (
    <SummaryRoot>
      <MessagesArea>
        <MessagesScroll ref={containerRef} onScroll={handleScroll}>
          <ChatContainer>
            {displayed.map((msg, i) => (
              <MessageRow key={i}>
                {i === 0 && (
                  <AvatarDot>
                    <AutoAwesomeIcon />
                  </AvatarDot>
                )}
                {i !== 0 && <Box sx={{ width: 24, flexShrink: 0 }} />}
                <MessageBubble alert={msg.alert}>
                  <MessageContent text={msg.text} />
                </MessageBubble>
              </MessageRow>
            ))}

            {showTyping && (
              <MessageRow>
                {visibleCount === 0 ? (
                  <AvatarDot>
                    <AutoAwesomeIcon />
                  </AvatarDot>
                ) : (
                  <Box sx={{ width: 24, flexShrink: 0 }} />
                )}
                <TypingIndicator>
                  <span />
                  <span />
                  <span />
                </TypingIndicator>
              </MessageRow>
            )}

            {/* User message bubbles for actions taken */}
            {userMessages.map((msg, i) => (
              <MessageRow key={`user-${i}`} sx={{ justifyContent: "flex-end" }}>
                <UserBubble>{msg.label}</UserBubble>
              </MessageRow>
            ))}

            {/* Revealed hidden messages */}
            {revealedMessages.map((msg, i) => (
              <MessageRow key={`revealed-${i}`}>
                <Box sx={{ width: 24, flexShrink: 0 }} />
                <MessageBubble>
                  <MessageContent text={msg} />
                </MessageBubble>
              </MessageRow>
            ))}

            {/* Data-reveal action chips — inline at end of conversation */}
            {animationDone && Object.keys(hiddenMessages).length > 0 && (
              <ActionsRow>
                {Object.entries(hiddenMessages).map(([key, entry], i) => (
                  <ActionChip
                    key={`reveal-${key}`}
                    size="small"
                    variant="outlined"
                    style={{ animationDelay: `${i * 80}ms` }}
                    onClick={() => handleReveal(key, entry.label)}
                  >
                    {entry.label}
                  </ActionChip>
                ))}
              </ActionsRow>
            )}
          </ChatContainer>
        </MessagesScroll>

        {hasMore && (
          <ScrollHintBtn onClick={scrollToBottom}>
            <KeyboardArrowDownIcon />
            Ver mais
          </ScrollHintBtn>
        )}
      </MessagesArea>

      {hasFooterActions && (
        <ActionsFooter>
          {availableActions.length > 0 && (
            <ActionsRow>
              {availableActions.map((action, i) => (
                <ActionChip
                  key={action.key}
                  startIcon={action.icon}
                  size="small"
                  chipcolor={action.color}
                  style={{ animationDelay: `${i * 80}ms` }}
                  onClick={() =>
                    handleAction(action.label, action.icon, action.handler)
                  }
                >
                  {action.label}
                  {action.shortcut && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        opacity: 0.5,
                        fontSize: "0.68rem",
                        fontFamily: "monospace",
                        fontWeight: 500,
                      }}
                    >
                      {action.shortcut}
                    </Box>
                  )}
                </ActionChip>
              ))}
            </ActionsRow>
          )}
        </ActionsFooter>
      )}
    </SummaryRoot>
  );
}
